import React from "react";
import { Card } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { getBreakdownChartData } from "./dashboardStatisticsUtils";

const CHART_COLORS = ["#4A90E2", "#FFA726", "#4ECDC4", "#66BB6A", "#E57373", "#BA68C8", "#4DD0E1", "#FFD54F"];

export const DashboardChartCard = ({ title, icon, children, className = "" }) => (
  <Card className={`shadow-sm border-0 mb-4 analytics-card dashboard-chart-card ${className}`}>
    <Card.Header className="bg-light border-0 py-2 px-3">
      <span className="fw-semibold text-secondary small">
        {icon && <i className={`bi ${icon} me-2`} />}
        {title}
      </span>
    </Card.Header>
    <Card.Body className="px-3 pb-3">{children}</Card.Body>
  </Card>
);

export const DashboardHorizontalBarChart = ({ title, icon, items, emptyLabel }) => {
  const { t } = useTranslation();
  const data = getBreakdownChartData(items);
  const max = Math.max(...data.map((d) => d.count), 1);

  if (!data.length) {
    return (
      <DashboardChartCard title={title} icon={icon}>
        <div className="dashboard-chart-empty">
          <i className="bi bi-bar-chart d-block mb-2 fs-4" />
          {emptyLabel || t("app.analytics.chartNoData")}
        </div>
      </DashboardChartCard>
    );
  }

  return (
    <DashboardChartCard title={title} icon={icon}>
      {data.map((item, idx) => {
        const pct = Math.round((item.count / max) * 100);
        const color = CHART_COLORS[idx % CHART_COLORS.length];
        return (
          <div key={item.id} className="dashboard-h-bar-row" title={`${item.name}: ${item.count}`}>
            <span className="dashboard-h-bar-label">{item.name}</span>
            <div className="dashboard-h-bar-track">
              <div className="dashboard-h-bar-fill" style={{ width: `${pct}%`, background: color }} />
            </div>
            <span className="dashboard-h-bar-value">{item.count}</span>
          </div>
        );
      })}
    </DashboardChartCard>
  );
};

export const DashboardDonutChart = ({ title, icon, items, centerLabel, emptyLabel }) => {
  const { t } = useTranslation();
  const data = getBreakdownChartData(items);
  const total = data.reduce((s, d) => s + d.count, 0);

  if (!data.length || total === 0) {
    return (
      <DashboardChartCard title={title} icon={icon}>
        <div className="dashboard-chart-empty">
          <i className="bi bi-pie-chart d-block mb-2 fs-4" />
          {emptyLabel || t("app.analytics.chartNoData")}
        </div>
      </DashboardChartCard>
    );
  }

  let startAngle = -90;
  const r = 42;
  const cx = 50;
  const cy = 50;
  const slices = data.map((item, idx) => {
    const angle = (item.count / total) * 360;
    const color = CHART_COLORS[idx % CHART_COLORS.length];
    let path = null;

    if (angle >= 359.99) {
      path = null;
    } else if (angle > 0) {
      const startRad = (Math.PI * startAngle) / 180;
      const endRad = (Math.PI * (startAngle + angle)) / 180;
      const x1 = cx + r * Math.cos(startRad);
      const y1 = cy + r * Math.sin(startRad);
      const x2 = cx + r * Math.cos(endRad);
      const y2 = cy + r * Math.sin(endRad);
      const largeArc = angle > 180 ? 1 : 0;
      path = `M${cx},${cy} L${x1},${y1} A${r},${r} 0 ${largeArc} 1 ${x2},${y2} Z`;
    }
    startAngle += angle;
    return { path, color, label: item.name, value: item.count, fullCircle: angle >= 359.99 };
  });

  return (
    <DashboardChartCard title={title} icon={icon}>
      <div className="d-flex flex-column flex-md-row align-items-center gap-4">
        <div className="dashboard-donut-wrap">
          <svg width={200} height={200} viewBox="0 0 100 100" aria-hidden="true">
            {slices.map((s, i) =>
              s.fullCircle ? (
                <circle key={i} cx={cx} cy={cy} r={r} fill={s.color} stroke="#fff" strokeWidth="1" />
              ) : s.path ? (
                <path key={i} d={s.path} fill={s.color} stroke="#fff" strokeWidth="1" />
              ) : null
            )}
            <circle cx={cx} cy={cy} r={26} fill="#fff" />
          </svg>
          <div className="dashboard-donut-center">
            <span className="dashboard-donut-center-total">{total}</span>
            <span className="dashboard-donut-center-label">{centerLabel || t("app.analytics.total")}</span>
          </div>
        </div>
        <div className="dashboard-legend flex-grow-1">
          {slices.map((s, i) => (
            <div key={i} className="dashboard-legend-item">
              <span className="dashboard-legend-swatch" style={{ background: s.color }} />
              <span>
                {s.label}{" "}
                <strong>
                  {s.value} ({total > 0 ? ((s.value / total) * 100).toFixed(1) : 0}%)
                </strong>
              </span>
            </div>
          ))}
        </div>
      </div>
    </DashboardChartCard>
  );
};

export const DashboardComparisonChart = ({ title, icon, bars }) => {
  const { t } = useTranslation();
  const valid = (bars || []).filter((b) => b.value != null);
  const max = Math.max(...valid.map((b) => Number(b.value) || 0), 1);

  if (!valid.length) {
    return (
      <DashboardChartCard title={title} icon={icon}>
        <div className="dashboard-chart-empty">{t("app.analytics.chartNoData")}</div>
      </DashboardChartCard>
    );
  }

  return (
    <DashboardChartCard title={title} icon={icon}>
      <div className="dashboard-comparison-bars">
        {valid.map((bar, idx) => {
          const val = Number(bar.value) || 0;
          const height = Math.max(8, Math.round((val / max) * 140));
          return (
            <div key={bar.key || idx} className="dashboard-comparison-col">
              <div className="dashboard-comparison-bar-wrap">
                <div
                  className="dashboard-comparison-bar"
                  style={{ height: `${height}px`, background: bar.color || CHART_COLORS[idx % CHART_COLORS.length] }}
                  title={`${bar.label}: ${bar.displayValue ?? val}`}
                />
              </div>
              <span className="dashboard-comparison-label">{bar.label}</span>
              <span className="dashboard-comparison-value">{bar.displayValue ?? val}</span>
            </div>
          );
        })}
      </div>
    </DashboardChartCard>
  );
};

export const DashboardGenderSplitChart = ({ genderRatio, genderBreakdown }) => {
  const { t } = useTranslation();
  const gr = genderRatio || {};
  const male = Number(gr.male) || 0;
  const female = Number(gr.female) || 0;
  const total = Number(gr.total) || male + female || 0;
  const unspecified = getBreakdownChartData(genderBreakdown).find((g) => /unspecified/i.test(g.name))?.count || 0;

  if (total === 0 && unspecified === 0) {
    return (
      <DashboardChartCard title={t("app.analytics.genderRatio")} icon="bi-gender-ambiguous">
        <div className="dashboard-chart-empty">{t("app.analytics.chartNoData")}</div>
      </DashboardChartCard>
    );
  }

  const malePct = total > 0 ? (male / total) * 100 : 0;
  const femalePct = total > 0 ? (female / total) * 100 : 0;
  const otherPct = Math.max(0, 100 - malePct - femalePct);

  return (
    <DashboardChartCard title={t("app.analytics.genderRatio")} icon="bi-gender-ambiguous">
      <div className="dashboard-gender-split">
        {malePct > 0 && <div className="dashboard-gender-split-segment" style={{ width: `${malePct}%`, background: "#4A90E2" }} title={`${t("app.analytics.male")}: ${male}`} />}
        {femalePct > 0 && <div className="dashboard-gender-split-segment" style={{ width: `${femalePct}%`, background: "#E57373" }} title={`${t("app.analytics.female")}: ${female}`} />}
        {otherPct > 0 && <div className="dashboard-gender-split-segment" style={{ width: `${otherPct}%`, background: "#94a3b8" }} title={`${unspecified}`} />}
      </div>
      <div className="row g-3 text-center">
        <div className="col-4">
          <div className="fw-bold fs-4 text-primary">{male}</div>
          <div className="text-muted small">{t("app.analytics.male")}</div>
          <div className="small">{malePct.toFixed(1)}%</div>
        </div>
        <div className="col-4">
          <div className="fw-bold fs-4" style={{ color: "#E57373" }}>
            {female}
          </div>
          <div className="text-muted small">{t("app.analytics.female")}</div>
          <div className="small">{femalePct.toFixed(1)}%</div>
        </div>
        <div className="col-4">
          <div className="fw-bold fs-4">{total}</div>
          <div className="text-muted small">{t("app.analytics.totalWithGender")}</div>
        </div>
      </div>
    </DashboardChartCard>
  );
};
